import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  Select,
  Spinner,
  Textarea,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Scissors, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ConfirmDialog, FormError, FormField, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  CATALOG_ROUTES,
  useCategories,
  useCreateService,
  useDeleteService,
  useServiceDetail,
  useUpdateService,
} from '../api';

const serviceFormSchema = z.object({
  name: z.string().min(2, 'Informe o nome').max(120),
  description: z.string().max(1000),
  categoryId: z.string(),
  unitId: z.string(),
  price: z.coerce.number().nonnegative('Preco invalido'),
  durationMinutes: z.coerce.number().int().positive('Informe a duracao'),
  maxDiscountPercent: z.coerce.number().int().min(0).max(100),
  autoDiscountPercent: z.coerce.number().int().min(0).max(100),
  isActive: z.boolean(),
});

type ServiceFormValues = z.infer<typeof serviceFormSchema>;

export function ServiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  useDocumentTitle(isEdit ? 'Editar servico' : 'Novo servico');

  const navigate = useNavigate();
  const { user } = useSession();
  const units = user?.units ?? [];

  const categories = useCategories({ page: 1 });
  const existing = useServiceDetail(id);
  const createService = useCreateService();
  const updateService = useUpdateService();
  const deleteService = useDeleteService();
  const [pendingRemoval, setPendingRemoval] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceFormSchema),
    values: existing.data
      ? {
          name: existing.data.name,
          description: existing.data.description ?? '',
          categoryId: existing.data.categoryId ?? '',
          unitId: existing.data.unitId ?? '',
          price: existing.data.price,
          durationMinutes: existing.data.durationMinutes,
          maxDiscountPercent: existing.data.maxDiscountPercent,
          autoDiscountPercent: existing.data.autoDiscountPercent,
          isActive: existing.data.isActive,
        }
      : undefined,
    defaultValues: {
      name: '',
      description: '',
      categoryId: '',
      unitId: '',
      price: 0,
      durationMinutes: 30,
      maxDiscountPercent: 0,
      autoDiscountPercent: 0,
      isActive: true,
    },
  });

  const mutationError = createService.error ?? updateService.error;

  if (isEdit && existing.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (isEdit && existing.isError) {
    return <p className="text-sm text-destructive">{toErrorMessage(existing.error)}</p>;
  }

  function onSubmit(values: ServiceFormValues) {
    const input = {
      ...values,
      categoryId: values.categoryId || undefined,
      unitId: values.unitId || undefined,
      images: [],
    };

    if (isEdit && id) {
      updateService.mutate({ id, input }, { onSuccess: () => navigate(CATALOG_ROUTES.SERVICES) });
      return;
    }

    createService.mutate(input, { onSuccess: () => navigate(CATALOG_ROUTES.SERVICES) });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Scissors />}
        title={isEdit ? 'Editar servico' : 'Novo servico'}
        description="Ficha tecnica do servico: preco, duracao e politica de desconto."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to={CATALOG_ROUTES.SERVICES}>
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Link>
            </Button>

            {/* Excluir mora aqui, no cadastro: na lista do celular ele era
                mais um botao pequeno repetido linha a linha. */}
            {isEdit ? (
              <Button
                variant="ghost"
                disabled={deleteService.isPending}
                onClick={() => setPendingRemoval(true)}
              >
                <Trash2 className="h-4 w-4" />
                Excluir
              </Button>
            ) : null}
          </>
        }
      />

      <Card variant="flush" className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">Dados do servico</CardTitle>
          <CardDescription>
            Sem unidade, o servico vale para todas as unidades da empresa.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormError message={mutationError ? toErrorMessage(mutationError) : null} />

            <FormField label="Nome" error={errors.name?.message} {...register('name')} />

            <div className="space-y-2">
              <Label htmlFor="description">Descricao</Label>
              <Textarea id="description" {...register('description')} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="categoryId">Categoria</Label>
                <Select id="categoryId" {...register('categoryId')}>
                  <option value="">Sem categoria</option>
                  {(categories.data?.data ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </div>

              {units.length > 1 ? (
                <div className="space-y-2">
                  <Label htmlFor="unitId">Unidade</Label>
                  <Select id="unitId" {...register('unitId')}>
                    <option value="">Todas as unidades</option>
                    {units.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Preco (R$)"
                type="number"
                step="0.01"
                min="0"
                error={errors.price?.message}
                {...register('price')}
              />
              <FormField
                label="Duracao media (min)"
                type="number"
                min="1"
                error={errors.durationMinutes?.message}
                {...register('durationMinutes')}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Desconto maximo (%)"
                type="number"
                min="0"
                max="100"
                error={errors.maxDiscountPercent?.message}
                {...register('maxDiscountPercent')}
              />
              <FormField
                label="Desconto promocional automatico (%)"
                type="number"
                min="0"
                max="100"
                error={errors.autoDiscountPercent?.message}
                {...register('autoDiscountPercent')}
              />
            </div>

            <Checkbox label="Servico ativo" {...register('isActive')} />

            <Button type="submit" disabled={createService.isPending || updateService.isPending}>
              {createService.isPending || updateService.isPending ? <Spinner /> : null}
              {isEdit ? 'Salvar alteracoes' : 'Criar servico'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingRemoval}
        title="Excluir servico"
        message="O servico sai do catalogo. Esta acao nao pode ser desfeita."
        loading={deleteService.isPending}
        onClose={() => setPendingRemoval(false)}
        onConfirm={() => {
          if (id) {
            deleteService.mutate(id, { onSuccess: () => navigate(CATALOG_ROUTES.SERVICES) });
          }
          setPendingRemoval(false);
        }}
      />
    </div>
  );
}
